import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'reactflow';
import { useWorkflowStore } from '../../store/workflowStore';
import { NODE_NAMES_AVAILABLE } from '../../store/canvasNavStore';
import { shortNodeNumber } from '../../utils/graphPaths';

/**
 * Chip flotante arriba del nodo: "#21 · Lumière se presenta".
 *
 * Vive DENTRO del nodo, así que se mueve con él y escala con el zoom sin
 * cálculos extra. Con zoom lejano se esconde (el texto sería ilegible).
 * El nombre es opcional: sin nombre muestra sólo el número.
 *
 * Clic para editar · Enter guarda · Esc cancela.
 */
const HIDE_BELOW_ZOOM = 0.45;
const MAX_LEN = 60;

export const NodeChip = ({ nodeId }) => {
  const zoom = useStore((s) => s.transform[2]);
  const name = useWorkflowStore(
    (s) => s.nodes.find((n) => n.id === nodeId)?.data?.name || ''
  );
  const updateNodeData = useWorkflowStore((s) => s.updateNodeData);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEditing = useCallback((event) => {
    event.stopPropagation();
    setDraft(name);
    setEditing(true);
  }, [name]);

  const commit = useCallback(() => {
    const next = draft.trim().slice(0, MAX_LEN);
    if (next !== name) updateNodeData(nodeId, { name: next || null });
    setEditing(false);
  }, [draft, name, nodeId, updateNodeData]);

  const onKeyDown = useCallback((event) => {
    event.stopPropagation();
    if (event.key === 'Enter') commit();
    else if (event.key === 'Escape') setEditing(false);
  }, [commit]);

  if (!NODE_NAMES_AVAILABLE) return null;
  if (zoom < HIDE_BELOW_ZOOM && !editing) return null;

  const number = shortNodeNumber(nodeId);

  return (
    <div
      className={`node-chip nodrag nopan ${editing ? 'editing' : ''}`}
      onClick={startEditing}
      onDoubleClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      title={name ? `#${number} · ${name}` : `#${number} — clic para nombrarlo`}
      data-testid={`node-chip-${nodeId}`}
    >
      <span className="node-chip-num">#{number}</span>
      {editing ? (
        <input
          ref={inputRef}
          className="node-chip-input"
          value={draft}
          maxLength={MAX_LEN}
          placeholder="Nombre del nodo"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
          data-testid={`node-chip-input-${nodeId}`}
        />
      ) : (
        name && <span className="node-chip-name">{name}</span>
      )}
    </div>
  );
};

export default NodeChip;
